<script setup lang="ts">
import {computed, getCurrentInstance, onBeforeUnmount, ref, watch} from 'vue'
import type {Placement} from '@floating-ui/vue'
import {useMutationObserver, useResizeObserver} from '@vueuse/core'
import TooltipPopperComponent from './TooltipPopperComponent.vue'
import {useTooltipTrigger} from '../composables/useTooltipTrigger'
import {cancelLayoutRead, scheduleLayoutRead} from '@toolify/client/src/modules/ui/functions/scheduleLayoutRead'

interface Props {
  forceShow?: boolean
  lines?: number
  fieldValue?: string|null
  placement?: Placement
  richContent?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  forceShow: false,
  lines: 1,
  fieldValue: null,
  placement: 'top',
  richContent: false,
})

interface Emits {
  (e: 'ellipsisChange', isEllipsisActive: boolean): void
}
const emit = defineEmits<Emits>()
const textContainer = ref<HTMLElement | null>(null)
const text = ref('')
const isForceShownWhileTruncated = ref(false)
const hasEllipsisChangeListener = Boolean(getCurrentInstance()?.vnode.props?.onEllipsisChange)

const isTextTruncated = (): boolean => {
  const container = textContainer.value
  if(!container) {
    return false
  }
  const field = container.querySelector('input, textarea') as HTMLInputElement | null
  const measured = field || container
  const isTruncated = props.lines > 1 && !field
    ? container.scrollHeight > container.clientHeight
    : measured.scrollWidth > measured.clientWidth
  if(isTruncated) {
    text.value = props.fieldValue ?? (field ? field.value : (container.textContent || ''))
  }
  return isTruncated
}

const {isShown, popperId} = useTooltipTrigger(textContainer, {
  trigger: () => props.forceShow ? 'manual' : 'hover',
  visible: () => isForceShownWhileTruncated.value,
  isDisabled: () => false,
  canShow: isTextTruncated,
})

watch(() => props.forceShow, (forceShow) => {
  isForceShownWhileTruncated.value = forceShow && isTextTruncated()
}, {
  immediate: true,
  flush: 'post',
})

if(hasEllipsisChangeListener) {
  const isEllipsisActive = ref(false)
  const checkEllipsis = () => {
    isEllipsisActive.value = isTextTruncated()
  }
  const scheduleEllipsisCheck = () => {
    scheduleLayoutRead(checkEllipsis)
  }
  useResizeObserver(textContainer, scheduleEllipsisCheck)
  useMutationObserver(textContainer, scheduleEllipsisCheck, {
    childList: true,
    characterData: true,
    subtree: true,
  })
  watch([() => props.lines, () => props.fieldValue], scheduleEllipsisCheck)
  watch(isEllipsisActive, (value) => {
    emit('ellipsisChange', value)
  })
  scheduleEllipsisCheck()
  onBeforeUnmount(() => {
    cancelLayoutRead(checkEllipsis)
  })
}

const truncateStyles = computed(() => ({'--truncate-lines': props.lines}))
</script>

<template>
  <div
    ref="textContainer"
    class="truncate-text"
    :class="{'is-multiline': lines > 1}"
    :style="truncateStyles"
    :aria-describedby="isShown ? popperId : undefined"
  >
    <slot/>
    <TooltipPopperComponent
      v-if="isShown"
      :reference="textContainer"
      :popperId="popperId"
      :placement="props.placement"
      :richContent="props.richContent"
    >
      <slot name="tooltip">{{text}}</slot>
    </TooltipPopperComponent>
  </div>
</template>

<style scoped lang="scss">
  .truncate-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &.is-multiline {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: var(--truncate-lines);
      line-clamp: var(--truncate-lines);
      white-space: normal;
      text-overflow: ellipsis;
    }
  }
</style>
